import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Eye, Heart, MessageCircle, Share2, Bookmark, Clock } from "lucide-react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { ReelCard } from "@/components/ReelCard";
import { SkeletonCard } from "@/components/Skeletons";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api from "@/lib/api";
import { formatNumber, formatDate } from "@/lib/format";
import { useT } from "@/i18n";
import { useNavigate } from "react-router-dom";

const SORTS = ["recent", "views", "likes", "comments", "shares", "engagement", "score"];

export function Reels() {
  const { t } = useT();
  const navigate = useNavigate();
  const [sort, setSort] = useState("recent");
  const [selected, setSelected] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ["reels", sort],
    queryFn: async () => (await api.get(`/reels?sort=${sort}`)).data,
    retry: false,
  });

  const stat = (icon, val) => (<span className="flex items-center gap-1.5 text-sm">{icon}{formatNumber(val)}</span>);

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="font-display text-2xl font-bold tracking-tight sm:text-3xl">{t("reels.title")}</h1>
            <p className="mt-1 text-muted-foreground">{t("reels.subtitle")}</p>
          </div>
          <div className="w-full sm:w-56">
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger data-testid="reels-sort" className="rounded-xl"><SelectValue placeholder={t("reels.sortBy")} /></SelectTrigger>
              <SelectContent>
                {SORTS.map((s) => <SelectItem key={s} value={s} data-testid={`sort-${s}`}>{t(`reels.sort.${s}`)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        {isLoading ? (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {Array.from({ length: 8 }).map((_, i) => <SkeletonCard key={i} className="aspect-[3/4]" />)}
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4" data-testid="reels-grid">
            {data?.reels.map((r, i) => <ReelCard key={r.reel_id} reel={r} index={i} onClick={() => setSelected(r)} />)}
          </div>
        )}
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-lg" data-testid="reel-detail-dialog">
          {selected && (
            <div>
              <DialogTitle className="sr-only">{selected.topic}</DialogTitle>
              <div className="overflow-hidden rounded-xl">
                <img src={selected.thumbnail_url} alt="" className="aspect-video w-full object-cover" />
              </div>
              <div className="mt-4 flex items-start justify-between gap-3">
                <div>
                  <div className="text-xs font-mono uppercase tracking-wide text-brand-700 dark:text-brand-400">{selected.topic}</div>
                  <h3 className="mt-1 font-display text-lg font-semibold leading-snug">{selected.hook}</h3>
                  <p className="mt-1 text-xs text-muted-foreground">{formatDate(selected.published_at)} · {selected.duration_sec}s</p>
                </div>
                <div className="shrink-0 text-center">
                  <div className="font-display text-3xl font-bold text-brand-700 dark:text-brand-400">{selected.growth_score}</div>
                  <div className="text-[10px] uppercase text-muted-foreground">{t("dashboard.growthScore")}</div>
                </div>
              </div>
              <div className="mt-4 grid grid-cols-3 gap-3 rounded-xl bg-muted/50 p-4 text-muted-foreground">
                {stat(<Eye className="h-4 w-4" />, selected.views)}
                {stat(<Heart className="h-4 w-4" />, selected.likes)}
                {stat(<MessageCircle className="h-4 w-4" />, selected.comments)}
                {stat(<Share2 className="h-4 w-4" />, selected.shares)}
                {stat(<Bookmark className="h-4 w-4" />, selected.saves)}
                {stat(<Clock className="h-4 w-4" />, selected.duration_sec)}
              </div>
              <div className="mt-4 rounded-xl border border-border p-4">
                <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-2">{t("reels.aiAnalysis")}</div>
                <div className="space-y-1.5 text-sm">
                  <div><span className="text-muted-foreground">{t("reels.topic")}: </span>{selected.topic}</div>
                  <div><span className="text-muted-foreground">{t("reels.format")}: </span>{selected.style}</div>
                  <div><span className="text-muted-foreground">{t("reels.hook")}: </span>"{selected.hook}"</div>
                  <div><span className="text-muted-foreground">{t("common.duration")}: </span>{selected.duration_sec} {t("common.seconds")}</div>
                </div>
                <p className="mt-3 rounded-lg bg-brand-500/10 p-3 text-xs text-brand-700 dark:text-brand-300">
                  {selected.views_delta >= 0 ? `+${selected.views_delta}` : selected.views_delta}% rispetto alla tua media.
                </p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
